/**
 * These helpers are built to add support to the username/password signup on the
 * client using a GraphQL mutation.
 *
 * We also calculate the continueUrl depending on different factors:
 *  - We use classCodes to join a certain classroom.
 *  - Is the user a teacher, parent or learner.
 *
 * @see https://khanacademy.atlassian.net/wiki/spaces/ENG/pages/925467867/Sign+up+with+username+password
 */
import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import gql from "graphql-tag";
import * as React from "react";

import Link from "@khanacademy/wonder-blocks-link";
import {
    joinClassroomsPostlogin,
    roleToUserRole,
    setAnalyticsCookies,
    type Role,
} from "@repo/auth-shared";
import type {ApolloGqlClient} from "@repo/data-gql-apollo";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";
import {Errors, KAError} from "@repo/logging";

import ApolloGqlError from "~/data-access/gql/apollo-gql-error.ts";

import type {
    signupAdultWithPasswordMutationType,
    signupDistrictsWithPasswordMutationType,
    signupLearnerWithPasswordMutationType,
    signupUnderAgeGateMutationType,
} from "./__graphql-types__/index.ts";

/**
 * Given an error code, return a user friendly error message.
 *
 * @param {string} errorCode The KA signup error code.
 * @returns {string} The user friendly message.
 */
export const getSignupErrorMessageByCode = (
    errorCode: string,
): React.ReactElement => {
    switch (errorCode) {
        case "INVALID_FIRSTNAME":
            // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'ReactElement<any, string | JSXElementConstructor<any>>'.
            return t`Please tell us your first name.`;
        case "INVALID_LASTNAME":
            // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'ReactElement<any, string | JSXElementConstructor<any>>'.
            return t`Please tell us your last name.`;
        case "INVALID_PASSWORD":
            // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'ReactElement<any, string | JSXElementConstructor<any>>'.
            return t`Sorry, but that password's too weak.`;
        case "INVALID_EMAIL":
            // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'ReactElement<any, string | JSXElementConstructor<any>>'.
            return t`This email doesn't appear to be formatted correctly.`;
        case "EXISTING_EMAIL":
            return (
                <Trans>
                    There's already an account with that email. If it's yours,{" "}
                    <Link href="/login">log in</Link> now.
                </Trans>
            );
        case "EXISTING_THIRD_PARTY_EMAIL":
            return (
                <Trans>
                    There's already an account with that email. If it's yours,{" "}
                    <Link href="/login">log in</Link> with your third-party
                    account (Google, Facebook, Apple, etc.).
                </Trans>
            );
        case "ALREADY_LOGGED_IN":
            // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'ReactElement<any, string | JSXElementConstructor<any>>'.
            return t`You are currently logged in with another account. Please log out of that account and try again.`;
        case "INTERNAL":
        case "INVALID_INPUT":
        case "UNDER_AGE_GATE":
        case "TEMPORARY_EMAIL":
        default:
            // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'ReactElement<any, string | JSXElementConstructor<any>>'.
            return t`Oops! Something went wrong. Please try again.`;
    }
};

export const SIGNUP_LEARNER_WITH_PASSWORD_MUTATION: GqlOperation<
    signupLearnerWithPasswordMutationType["response"],
    signupLearnerWithPasswordMutationType["variables"]
> = gqlOp(gql`
    mutation signupLearnerWithPasswordMutation(
        $email: String!
        $password: String!
        $firstname: String!
        $lastname: String!
        $birthdate: Date!
    ) {
        signupLearnerWithPassword(
            email: $email
            password: $password
            firstname: $firstname
            lastname: $lastname
            birthdate: $birthdate
        ) {
            user {
                id
                kaid
                canAccessDistrictsHomepage
                isTeacher
                hasUnresolvedInvitations
                preferredKaLocale {
                    id
                    kaLocale
                    status
                }
            }
            error {
                code
            }
        }
    }
`);

export const SIGNUP_ADULT_WITH_PASSWORD_MUTATION: GqlOperation<
    signupAdultWithPasswordMutationType["response"],
    signupAdultWithPasswordMutationType["variables"]
> = gqlOp(gql`
    mutation signupAdultWithPasswordMutation(
        $email: String!
        $password: String!
        $firstname: String!
        $lastname: String!
        $role: UserRole!
        $birthdate: Date
    ) {
        signupAdultWithPassword(
            email: $email
            password: $password
            firstname: $firstname
            lastname: $lastname
            role: $role
            birthdate: $birthdate
        ) {
            user {
                id
                kaid
                canAccessDistrictsHomepage
                isTeacher
                hasUnresolvedInvitations
                preferredKaLocale {
                    id
                    kaLocale
                    status
                }
            }
            error {
                code
            }
        }
    }
`);

/**
 * Signs up a new user using a GraphQL mutation, and optionally tries to join a
 * classroom.
 *
 * @param {Options} options The variables that will be sent to the mutation.
 * @param {string} options.email The user email.
 * @param {string} options.firstname The user firstname.
 * @param {string} options.lastname The user lastname.
 * @param {string} options.password The user password.
 * @param {string} options.birthdate The user birthdate.
 * @param {string} [options.birthdate] The optional user birthdate (used for
 * learners).
 * @param {string} [options.role] The optional user role (used for teachers or
 * parents).
 * @param {Array<string>} [options.signupCodes] An optional list of signupCodes
 * passed in the UI. e.g /join-class/ABC123.
 *
 * @returns {Promise<SignupTypeData>} The mutation result.
 */
export const signupWithPassword = (
    gqlClient: ApolloGqlClient,
    options: {
        // Shared fields used by the signupXXWithPassword mutations.
        email: string;
        firstname: string;
        lastname: string;
        password: string;
        // Used by signupLearnerWithPassword.
        birthdate?: string;
        // Used by signupAdultWithPassword.
        role?: Role;
        // Used by the joinClassroomsPostlogin mutation.
        signupCodes?: Array<string>;
    },
) => {
    const {birthdate, role, signupCodes, ...restOptions} = options;

    const handleResult = async (
        result?:
            | signupLearnerWithPasswordMutationType["response"]["signupLearnerWithPassword"]
            | signupAdultWithPasswordMutationType["response"]["signupAdultWithPassword"],
    ) => {
        if (result?.error) {
            // We pass the error code directly, so that code is mapped to an
            // UI readable message.
            throw new KAError(result.error.code, Errors.Internal);
        }

        if (!result?.user) {
            throw new KAError(
                "Failed to signup with password",
                Errors.Internal,
            );
        }

        const user = result?.user;

        if (user?.kaid) {
            setAnalyticsCookies(true, user.kaid);
        }

        // Only call the joinClassrooms mutation if the user is logged in
        // and is passing signupCodes.
        if (signupCodes && signupCodes.length) {
            const joinClassrooms = await joinClassroomsPostlogin(
                gqlClient,
                signupCodes,
            );
            return {
                user,
                joinClassrooms,
            };
        }

        return {
            user,
        };
    };

    const handleError = (error: Error) => {
        // We handle the exception thrown from within the mutation success
        // handler.
        if (error instanceof KAError && !(error instanceof ApolloGqlError)) {
            throw error;
        }

        // Otherwise we throw a generic error.
        throw new KAError("Failed to signup with password", Errors.Internal, {
            cause: error,
        });
    };

    const isLearner = !role || role === "learner";
    if (isLearner) {
        // Birthdate has to exist for this mutation.
        // @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
        const concreteBirthdate: string = birthdate;
        return gqlClient
            .mutate(SIGNUP_LEARNER_WITH_PASSWORD_MUTATION, {
                variables: {...restOptions, birthdate: concreteBirthdate},
            })
            .then((result) =>
                handleResult(result.data?.signupLearnerWithPassword),
            )
            .catch(handleError);
    }

    // Role has to exist for this mutation.
    const concreteUserRole = roleToUserRole(role);
    if (!concreteUserRole) {
        throw new KAError("Invalid role", Errors.Internal);
    }
    return gqlClient
        .mutate(SIGNUP_ADULT_WITH_PASSWORD_MUTATION, {
            variables: {...restOptions, role: concreteUserRole},
        })
        .then((result) => handleResult(result.data?.signupAdultWithPassword))
        .catch(handleError);
};

export const SIGNUP_UNDER_AGE_GATE_MUTATION: GqlOperation<
    signupUnderAgeGateMutationType["response"],
    signupUnderAgeGateMutationType["variables"]
> = gqlOp(gql`
    mutation signupUnderAgeGateMutation(
        $username: String!
        $password: String!
        $birthdate: Date!
        $parentEmail: String!
        $signupCodes: [String]
    ) {
        signupUnderAgeGate(
            username: $username
            password: $password
            birthdate: $birthdate
            parentEmail: $parentEmail
            signupCodes: $signupCodes
        ) {
            user {
                id
                kaid
                canAccessDistrictsHomepage
                isTeacher
                hasUnresolvedInvitations
                preferredKaLocale {
                    id
                    kaLocale
                    status
                }
                studentLists {
                    id
                    cacheId
                    signupCode
                    name
                }
            }
            error {
                code
            }
        }
    }
`);

/**
 * Translates a given error code to a readable message that will be displayed in
 * the UI.
 * @param {SignupUnderAgeGateErrorCode} errorCode The error code returned by the
 * mutation.
 *
 * @returns {string} The UI error message.
 */

/**
This function serves to get the error message from an unknown error.
This is meant to be used in conjunction with signUpUnderAgeGate but can be
used for other unknown errors as well. Since signUpUnderAgeGate can throw
KAErrors or plain Errors (with the relevant code potentially being in
different fields), we need a way to account for all of them.
 */
export const getErrorMessageFromUnknownError = (
    error: KAError | Error,
    fallbackMessage: string,
) => {
    const errorStrings = getErrorCodeToMessageMap();

    if (error instanceof KAError) {
        return errorStrings[error.originalMessage] || fallbackMessage;
    }

    if (error instanceof Error) {
        return errorStrings[error.message] || fallbackMessage;
    }
    return fallbackMessage;
};

export const getErrorCodeToMessageMap = () => {
    return {
        INTERNAL: t`Oops! Something went wrong. Please try again.`,
        EXISTING_USERNAME: t`That username isn't available.`,
        INVALID_PASSWORD: t`Sorry, but that password's too weak.`,
        ALREADY_LOGGED_IN: t`You are currently logged in with another account. Please log out of that account and try again.`,
        INVALID_BIRTHDATE: t`Invalid birthdate.`,
        OVER_AGE_GATE: t`Oops! Something went wrong. Please try again.`,
        CLASSROOM_NOT_FOUND: t`Invalid class code given.`,
        INVALID_PARENT_EMAIL: t`The parent or guardian's email appears to be invalid.`,
    };
};

export const getSignupUnderAgeGateErrorMessageByCode = (
    errorCode: string,
): string => {
    const errorStrings = getErrorCodeToMessageMap();

    // return the mapped message, but in the odd case that the mutation returns
    // a unhandled error, then we will return the default message.
    return (
        errorStrings[errorCode] ||
        t`Oops! Something went wrong. Please try again.`
    );
};

export interface UAGOptions {
    // Shared fields used by the signupXXWithPassword mutations.
    parentEmail: string;
    username: string;
    password: string;
    birthdate: string;
    signupCodes?: ReadonlyArray<string>;
}

/**
 * Signs up a new Under Age Gate user using a GraphQL mutation. This mutation
 * also attemps to join a classroom when the user tries to use signupCodes.
 *
 * @param {Options} options The variables that will be sent to the mutation.
 * @param {string} options.parentEmail Email of the parent or guardian for this
 * under age gate user.
 * @param {string} options.username Username selected by the user.
 * @param {string} options.password Password created by the user..
 * @param {string} options.birthdate The user birthdate.
 * @param {Array<string>} [options.signupCodes] An optional list of signupCodes
 * passed in the UI. e.g /join-class/ABC123.
 *
 * @returns {Promise<SignupTypeData>} The mutation result.
 */
export const signupUnderAgeGate = async (
    gqlClient: ApolloGqlClient,
    options: UAGOptions,
) => {
    try {
        const result = await gqlClient.mutate(SIGNUP_UNDER_AGE_GATE_MUTATION, {
            variables: options,
        });
        const {error} = (result.data && result.data.signupUnderAgeGate) || {
            error: null,
        };
        if (error) {
            // We pass the error code directly, so that code is mapped to an
            // UI readable message.
            throw new KAError(error.code, Errors.Internal);
        }

        const user = result.data?.signupUnderAgeGate?.user;

        if (!user) {
            throw new KAError(
                "Failed to signup under age gate user",
                Errors.Internal,
            );
        }

        const {studentLists, ...userData} = user;

        // Set analytics cookies.
        if (userData.kaid) {
            setAnalyticsCookies(true, userData.kaid);
        }

        // Return data in a way that calculateNextUrl can understand.
        if (studentLists && studentLists.length) {
            return {
                user: userData,
                joinClassrooms: {
                    errors: null,
                    studentLists,
                },
            };
        }

        return {
            user: userData,
            joinClassrooms: undefined,
        };
    } catch (error: any) {
        // We handle the exception thrown from within the mutation success
        // handler.
        if (error instanceof KAError && !(error instanceof ApolloGqlError)) {
            throw error;
        }

        // Otherwise we throw a generic error.
        throw new KAError(
            "Failed to signup under age gate user",
            Errors.Internal,
            {cause: error},
        );
    }
};

/**
 * Districts signup
 */

export const SIGNUP_DISTRICTS_WITH_PASSWORD_MUTATION: GqlOperation<
    signupDistrictsWithPasswordMutationType["response"],
    signupDistrictsWithPasswordMutationType["variables"]
> = gqlOp(gql`
    mutation signupDistrictsWithPasswordMutation(
        $email: String!
        $password: String!
        $firstname: String!
        $lastname: String!
        $role: UserRole
        $birthdate: Date
    ) {
        signupDistrictsWithPassword(
            email: $email
            password: $password
            firstname: $firstname
            lastname: $lastname
            role: $role
            birthdate: $birthdate
        ) {
            user {
                id
                kaid
                canAccessDistrictsHomepage
                isTeacher
                hasUnresolvedInvitations
                preferredKaLocale {
                    id
                    kaLocale
                    status
                }
            }
            error {
                code
            }
        }
    }
`);

export type SignupDistrictsWithPasswordErrorCode =
    | "INTERNAL"
    | "INVALID_FIRSTNAME"
    | "INVALID_LASTNAME"
    | "INVALID_PASSWORD"
    | "INVALID_EMAIL"
    | "EXISTING_EMAIL"
    | "EXISTING_THIRD_PARTY_EMAIL"
    | "ALREADY_LOGGED_IN";

/**
 * Translates a given error code to a readable message that will be displayed in
 * the UI.
 * @param {SignupDistrictsWithPasswordErrorCode} errorCode The error code
 * returned by the mutation.
 *
 * @returns {string} The UI error message.
 */
export const getDistrictsWithPasswordErrorMessageByCode = (
    errorCode: SignupDistrictsWithPasswordErrorCode,
): string => {
    const errorStrings: Partial<
        Record<SignupDistrictsWithPasswordErrorCode, string>
    > = {
        INTERNAL: t`Oops! Something went wrong. Please try again.`,
        INVALID_FIRSTNAME: t`Please tell us your first name.`,
        INVALID_LASTNAME: t`Please tell us your last name.`,
        INVALID_PASSWORD: t`Sorry, but that password's too weak.`,
        INVALID_EMAIL: t`This email doesn't appear to be formatted correctly.`,
        EXISTING_EMAIL: t`There’s already an account with that email. Select “Back” and choose “Log in to your existing account” instead`,
        EXISTING_THIRD_PARTY_EMAIL: t`There’s already an account with that email. Select “Back” and choose “Log in to your existing account” instead`,
        ALREADY_LOGGED_IN: t`You are currently logged in with another account. Please log out of that account and try again.`,
    };

    // return the mapped message, but in the odd case that the mutation returns
    // a unhandled error, then we will return the default message.
    return (
        errorStrings[errorCode] ||
        t`Oops! Something went wrong. Please try again.`
    );
};

/**
 * Signs a new districts user with an email and password. This mutation
 * also attemps to join a classroom when the user tries to use signupCodes.
 *
 * @param {Options} options The variables that will be sent to the mutation.
 * @param {string} options.email Districts user email.
 * @param {string} options.password Password created by the user.
 * @param {string} options.firstname User's firstname.
 * @param {string} options.lastname User's lastname.
 * @param {string} [options.role] The optional user role (used for teachers).
 * @param {string} [options.birthdate] The optional user birthdate (used for
 * learners).
 *
 * @returns {Promise<SignupTypeData>} The mutation result.
 */
export const signupDistrictsWithPassword = async (
    gqlClient: ApolloGqlClient,
    options: {
        email: string;
        password: string;
        firstname: string;
        lastname: string;
        role?: Role;
        birthdate?: string | null | undefined;
    },
) => {
    try {
        const result = await gqlClient.mutate(
            SIGNUP_DISTRICTS_WITH_PASSWORD_MUTATION,
            {
                variables: {...options, role: roleToUserRole(options.role)},
            },
        );
        const {error} = (result.data &&
            result.data.signupDistrictsWithPassword) || {
            error: null,
        };
        if (error) {
            // We pass the error code directly, so that code is mapped to an
            // UI readable message.
            throw new KAError(error.code, Errors.Internal);
        }

        const user =
            result.data &&
            result.data.signupDistrictsWithPassword &&
            result.data.signupDistrictsWithPassword.user;

        if (user) {
            if (user.kaid) {
                setAnalyticsCookies(true, user.kaid);
            }

            return {
                user,
                joinClassrooms: undefined,
            };
        }
        // Extra guard, but we should never reach here.
        return;
    } catch (error: any) {
        // We handle the exception thrown from within the mutation success
        // handler.
        if (error instanceof KAError && !(error instanceof ApolloGqlError)) {
            throw error;
        }

        // Otherwise we throw a generic error.
        throw new KAError("Failed to signup Districts user", Errors.Internal);
    }
};
